// ─────────────────────────────────────────────────────────────────
// Glass-i — Other Charges Master helpers (Stage 6OC.1).
//
// Pure ESM module — BOTH importable by the .jsx pages via Vite AND
// require-able by the node:test runner (same convention as
// frontend/src/pages/erp/purchase/purchaseHelpers.js). No React, no
// JSX, no AntD imports.
//
// Frontend mirror of the authoritative backend rule-engine:
//   backend/src/services/erp/otherCharges/otherChargesCalculation.service.js
//   backend/src/services/erp/otherCharges/otherCharges.validation.js
//
// The backend preview/audit-save API remains the source of truth for
// HOW a rule turns document lines into charge amounts. These helpers
// exist only to give the grid instant feedback while the operator
// types; the API recomputes on every save.
//
// Worked example (PER_SQM_MM):
//   ruleValue = 6, thickness = 6mm  → rate = 36 /SQM
//   ruleValue = 6, thickness = 12mm → rate = 72 /SQM
//
// Business dropdown values (material / glass / thickness / category)
// are NEVER hardcoded here — only the internal rule-engine enums +
// the system 'ALL' code are constants. The enum LABEL maps below are
// for human display only (enums, not business data).
// ─────────────────────────────────────────────────────────────────


// ── system / engine constants ───────────────────────────────────
const SYSTEM_CODE_ALL = 'ALL';
const SQM_TO_SQFT = 10.764;

// Size-based conditions require dimensionLimitMm (mirrors backend
// SIZE_BASED_CONDITIONS).
const SIZE_BASED_CONDITIONS = Object.freeze([
  'ANY_SIDE_EXCEEDS', 'WIDTH_EXCEEDS', 'HEIGHT_EXCEEDS',
  'SIZE_OR_AREA', 'SIZE_AND_AREA'
]);


// ── numeric helpers (mirror backend num / roundTo) ───────────────
function num(v, fb = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fb;
}

function bool(v, fb = false) {
  if (v === true || v === false) return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  if (v === 1 || v === '1') return true;
  if (v === 0 || v === '0') return false;
  return fb;
}

export function roundTo(value, digits) {
  const d = Number.isFinite(Number(digits))
    ? Math.max(0, Math.min(4, Math.trunc(Number(digits))))
    : 2;
  const f = Math.pow(10, d);
  // +Number.EPSILON guards against 1.005-style float underflow.
  return Math.round((num(value) + Number.EPSILON) * f) / f;
}


// ── geometry (mirror backend) ────────────────────────────────────
export function calculateLineSqm(widthMm, heightMm, qty) {
  const w = num(widthMm), h = num(heightMm), q = num(qty, 1);
  if (w <= 0 || h <= 0) return 0;
  return (w / 1000) * (h / 1000) * (q > 0 ? q : 1);
}

export function calculateLineSqft(sqm) {
  return num(sqm) * SQM_TO_SQFT;
}

// Fallback thickness extractor — first "<n> mm" match → number, else null.
export function parseThicknessMm(itemName) {
  if (!itemName) return null;
  const m = String(itemName).match(/(\d+(?:\.\d+)?)\s*mm/i);
  return m ? num(m[1], null) : null;
}

function getThicknessMm(line) {
  const fromMaster = num(line && line.thicknessMm, 0);
  if (fromMaster > 0) return fromMaster;
  const parsed = parseThicknessMm(line && line.itemName);
  return parsed && parsed > 0 ? parsed : 0;
}

function lineSqm(line) {
  const explicit = num(line && line.sqm, 0);
  if (explicit > 0) return explicit;
  return calculateLineSqm(line && line.widthMm, line && line.heightMm, line && line.qty);
}

function resolveAreaValue(rule, line) {
  if (rule && rule.areaBasis === 'CHARGEABLE_SQM') {
    const ch = num(line && line.chargeableSqm, 0);
    if (ch > 0) return ch;
  }
  return lineSqm(line);
}


// ── code-list matching (mirror backend) ──────────────────────────
// Normalise an applicability value into an array of UPPER codes.
// [] or ['ALL'] mean "match all".
export function normalizeCodeList(value) {
  let arr = value;
  if (arr === null || arr === undefined || arr === '') return [];
  if (typeof arr === 'string') {
    const s = arr.trim();
    if (s.startsWith('[')) {
      try { arr = JSON.parse(s); } catch (_e) { arr = s.split(','); }
    } else {
      arr = s.split(',');
    }
  }
  if (!Array.isArray(arr)) arr = [arr];
  const out = [];
  for (const item of arr) {
    if (item === null || item === undefined) continue;
    const c = String(item).trim().toUpperCase();
    if (c && !out.includes(c)) out.push(c);
  }
  return out;
}

// True when the rule list is empty, contains 'ALL', or explicitly
// contains the (upper-cased) line value.
export function listMatches(ruleList, lineValue) {
  const list = normalizeCodeList(ruleList);
  if (list.length === 0 || list.includes(SYSTEM_CODE_ALL)) return true;
  if (lineValue === null || lineValue === undefined || lineValue === '') return false;
  return list.includes(String(lineValue).trim().toUpperCase());
}

function isMaterialTypeApplicable(rule, line) {
  return listMatches(rule.applicableMaterialTypeCodes, line && line.materialTypeCode);
}
function isGlassTypeApplicable(rule, line) {
  return listMatches(rule.glassTypeCodes, line && line.glassTypeCode);
}
function isThicknessApplicable(rule, line) {
  const list = normalizeCodeList(rule.thicknessCodes);
  if (list.length === 0 || list.includes(SYSTEM_CODE_ALL)) return true;
  if (line) {
    if (line.thicknessCode && list.includes(String(line.thicknessCode).trim().toUpperCase())) return true;
    if (line.thicknessLabel && list.includes(String(line.thicknessLabel).trim().toUpperCase())) return true;
    const mm = getThicknessMm(line);
    if (mm > 0) {
      const asInt = String(mm).toUpperCase();
      const asMm = `${mm}MM`.toUpperCase();
      if (list.includes(asInt) || list.includes(asMm)) return true;
    }
  }
  return false;
}
function isCategoryApplicable(rule, line) {
  return listMatches(rule.categoryCodes, line && line.categoryCode);
}
// Glass colour family (clear/tinted/reflective/…) — mirrors the backend
// isGlassFamilyApplicable. No filter / ['ALL'] ⇒ applies everywhere.
function isGlassFamilyApplicable(rule, line) {
  return listMatches(rule.glassFamilyCodes, line && line.glassFamily);
}

// apply_on filters by broad material family.
function applyOnMatches(rule, line) {
  const scope = String(rule.applyOn || 'ALL').toUpperCase();
  if (scope === 'ALL') return true;
  const mt = String((line && line.materialTypeCode) || '').toUpperCase();
  switch (scope) {
    case 'GLASS_ONLY':    return mt === 'GLASS';
    case 'HARDWARE_ONLY': return mt === 'HARDWARE' || mt === 'ACCESSORY';
    case 'PROFILE_ONLY':  return mt === 'PROFILE' || mt === 'UPVC' || mt === 'ALUMINIUM';
    case 'SERVICES_ONLY': return mt === 'SERVICE' || mt === 'JOBWORK';
    default:              return true;
  }
}


// ── area slabs (mirror backend matchingAreaSlab) ─────────────────
// Highest matching slab wins. Returns { area, value, index } or null.
export function matchingAreaSlabClient(rule, areaValue) {
  const a = num(areaValue);
  const slabs = [
    { area: num(rule.slab1Area), value: num(rule.slab1Value), index: 1 },
    { area: num(rule.slab2Area), value: num(rule.slab2Value), index: 2 },
    { area: num(rule.slab3Area), value: num(rule.slab3Value), index: 3 }
  ].filter((s) => s.area > 0 && a >= s.area);
  if (slabs.length === 0) return null;
  return slabs.reduce((best, s) => (s.area > best.area ? s : best), slabs[0]);
}


// ── condition gate (mirror backend matchCondition) ───────────────
export function matchConditionClient(rule, line) {
  const ct = String(rule.conditionType || 'ALWAYS').toUpperCase();
  const w = num(line && line.widthMm);
  const h = num(line && line.heightMm);
  const limit = num(rule.dimensionLimitMm);
  const area = resolveAreaValue(rule, line);
  const sizeHit = (w > limit && limit > 0) || (h > limit && limit > 0);
  const widthHit = limit > 0 && w > limit;
  const heightHit = limit > 0 && h > limit;
  const areaLimitHit = num(rule.areaLimitSqm) > 0 && area > num(rule.areaLimitSqm);
  const slabHit = matchingAreaSlabClient(rule, area) !== null;

  switch (ct) {
    case 'ALWAYS':          return true;
    case 'WIDTH_EXCEEDS':   return widthHit;
    case 'HEIGHT_EXCEEDS':  return heightHit;
    case 'ANY_SIDE_EXCEEDS':return sizeHit;
    case 'AREA_EXCEEDS':    return areaLimitHit;
    case 'AREA_SLAB':       return slabHit;
    case 'SIZE_OR_AREA':    return sizeHit || areaLimitHit || slabHit;
    case 'SIZE_AND_AREA':   return sizeHit && (areaLimitHit || slabHit);
    default:                return true;
  }
}


// ── rule normalisation (DTO → clean shape with defaults) ─────────
export function normalizeOtherChargeRule(dto) {
  const safe = dto && typeof dto === 'object' ? dto : {};
  return {
    id:          safe.id || null,
    chargeCode:  safe.chargeCode != null ? String(safe.chargeCode).trim().toUpperCase() : '',
    chargeName:  safe.chargeName != null ? String(safe.chargeName) : '',
    description: safe.description != null ? String(safe.description) : '',

    applicableMaterialTypeCodes: normalizeCodeList(
      safe.applicableMaterialTypeCodes !== undefined
        ? safe.applicableMaterialTypeCodes
        : ['ALL']
    ),
    glassTypeCodes:  normalizeCodeList(
      safe.glassTypeCodes !== undefined ? safe.glassTypeCodes : ['ALL']
    ),
    thicknessCodes:  normalizeCodeList(
      safe.thicknessCodes !== undefined ? safe.thicknessCodes : ['ALL']
    ),
    categoryCodes:   normalizeCodeList(
      safe.categoryCodes !== undefined ? safe.categoryCodes : ['ALL']
    ),
    glassFamilyCodes: normalizeCodeList(
      safe.glassFamilyCodes !== undefined ? safe.glassFamilyCodes : ['ALL']
    ),

    moduleScope: safe.moduleScope ? String(safe.moduleScope).toUpperCase() : 'ALL',
    applyOn:     safe.applyOn ? String(safe.applyOn).toUpperCase() : 'ALL',

    mode:    safe.mode ? String(safe.mode).toUpperCase() : 'INR',
    valueBy: safe.valueBy ? String(safe.valueBy).toUpperCase() : 'FIXED',
    ruleValue: num(safe.ruleValue, 0),

    conditionType:    safe.conditionType ? String(safe.conditionType).toUpperCase() : 'ALWAYS',
    dimensionLimitMm: num(safe.dimensionLimitMm, 0),
    areaBasis:        safe.areaBasis ? String(safe.areaBasis).toUpperCase() : 'LINE_SQM',
    areaLimitSqm:     num(safe.areaLimitSqm, 0),
    chargeBase:       safe.chargeBase ? String(safe.chargeBase).toUpperCase() : 'FIXED_PER_DOCUMENT',
    displayAs:        safe.displayAs ? String(safe.displayAs).toUpperCase() : 'LINE_WISE',
    roundTo:          Number.isFinite(Number(safe.roundTo))
      ? Math.max(0, Math.min(4, Math.trunc(Number(safe.roundTo))))
      : 2,

    slab1Area:  num(safe.slab1Area, 0),
    slab1Value: num(safe.slab1Value, 0),
    slab2Area:  num(safe.slab2Area, 0),
    slab2Value: num(safe.slab2Value, 0),
    slab3Area:  num(safe.slab3Area, 0),
    slab3Value: num(safe.slab3Value, 0),

    taxable: bool(safe.taxable, true),
    gstRate: num(safe.gstRate, 18),
    hsnSac:  safe.hsnSac != null ? String(safe.hsnSac) : '',

    autoInclude:         bool(safe.autoInclude, true),
    allowRuntimeRate:    bool(safe.allowRuntimeRate, true),
    allowResultRateEdit: bool(safe.allowResultRateEdit, true),
    active:              bool(safe.active, true),
    // Stage 6OC — SHEET-scrap opt-in. Default true; carry through on EDIT.
    includeSheetScrap:   bool(safe.includeSheetScrap, true),
    sortOrder:           num(safe.sortOrder, 0),

    // Stage 6OC.2 / 6OC.3 — carry these through on EDIT so the form shows the
    // saved value instead of the 'ALL'/'RUNNING_TOTAL' default (otherwise a
    // re-save silently wipes a configured polish / compute base).
    computeBase:     safe.computeBase ? String(safe.computeBase).toUpperCase() : 'RUNNING_TOTAL',
    polishTypeCode:  safe.polishTypeCode ? String(safe.polishTypeCode).toUpperCase() : 'ALL',

    createdAt: safe.createdAt || null,
    updatedAt: safe.updatedAt || null
  };
}


// ── form → API ruleInput payload ─────────────────────────────────
export function buildOtherChargeRulePayload(formVals) {
  const v = formVals && typeof formVals === 'object' ? formVals : {};
  const codeList = (val) => {
    const list = normalizeCodeList(val !== undefined ? val : ['ALL']);
    return list.length === 0 ? ['ALL'] : list;
  };
  return {
    chargeCode:  v.chargeCode != null ? String(v.chargeCode).trim().toUpperCase() : '',
    chargeName:  v.chargeName != null ? String(v.chargeName).trim() : '',
    description: v.description != null ? String(v.description) : '',

    applicableMaterialTypeCodes: codeList(v.applicableMaterialTypeCodes),
    glassTypeCodes:              codeList(v.glassTypeCodes),
    thicknessCodes:              codeList(v.thicknessCodes),
    categoryCodes:               codeList(v.categoryCodes),
    glassFamilyCodes:            codeList(v.glassFamilyCodes),

    moduleScope: v.moduleScope ? String(v.moduleScope).toUpperCase() : 'ALL',
    applyOn:     v.applyOn ? String(v.applyOn).toUpperCase() : 'ALL',

    mode:    v.mode ? String(v.mode).toUpperCase() : 'INR',
    valueBy: v.valueBy ? String(v.valueBy).toUpperCase() : 'FIXED',
    ruleValue: num(v.ruleValue, 0),

    conditionType:    v.conditionType ? String(v.conditionType).toUpperCase() : 'ALWAYS',
    dimensionLimitMm: num(v.dimensionLimitMm, 0),
    areaBasis:        v.areaBasis ? String(v.areaBasis).toUpperCase() : 'LINE_SQM',
    areaLimitSqm:     num(v.areaLimitSqm, 0),
    chargeBase:       v.chargeBase ? String(v.chargeBase).toUpperCase() : 'FIXED_PER_DOCUMENT',
    displayAs:        v.displayAs ? String(v.displayAs).toUpperCase() : 'LINE_WISE',

    // Stage 6B.8A-3O — Formula + Area-Basis/UOM descriptors.
    formulaCode:     v.formulaCode ? String(v.formulaCode).toUpperCase() : 'FIXED',
    sourceColumn:    v.sourceColumn ? String(v.sourceColumn).toUpperCase() : 'CHARGEABLE',
    uom:             v.uom ? String(v.uom).toUpperCase() : 'SQM',
    quantityBasis:   v.quantityBasis ? String(v.quantityBasis).toUpperCase() : 'ALL_QTY',
    showResultMode:  v.showResultMode ? String(v.showResultMode).toUpperCase() : 'THICKNESS_WISE',
    // Stage 6OC.2 — base a Percent/Multiplier charge computes on.
    computeBase:     v.computeBase ? String(v.computeBase).toUpperCase() : 'RUNNING_TOTAL',
    // Stage 6OC.3 — single polish-type applicability ('ALL' or a polish code).
    polishTypeCode:  v.polishTypeCode ? String(v.polishTypeCode).toUpperCase() : 'ALL',
    displayGroup:    v.displayGroup != null && String(v.displayGroup).trim() !== ''
      ? String(v.displayGroup).trim().toUpperCase()
      : null,

    roundTo:          Number.isFinite(Number(v.roundTo))
      ? Math.max(0, Math.min(4, Math.trunc(Number(v.roundTo))))
      : 2,

    slab1Area:  num(v.slab1Area, 0),
    slab1Value: num(v.slab1Value, 0),
    slab2Area:  num(v.slab2Area, 0),
    slab2Value: num(v.slab2Value, 0),
    slab3Area:  num(v.slab3Area, 0),
    slab3Value: num(v.slab3Value, 0),

    taxable: bool(v.taxable, true),
    gstRate: num(v.gstRate, 18),
    hsnSac:  v.hsnSac != null ? String(v.hsnSac).trim() : '',

    autoInclude:         bool(v.autoInclude, true),
    allowRuntimeRate:    bool(v.allowRuntimeRate, true),
    allowResultRateEdit: bool(v.allowResultRateEdit, true),
    active:              bool(v.active, true),
    // Stage 6OC — SHEET-scrap opt-in (default true).
    includeSheetScrap:   bool(v.includeSheetScrap, true),
    sortOrder:           num(v.sortOrder, 0)
  };
}


// ── line normalisation ───────────────────────────────────────────
export function normalizeLineItem(line) {
  const safe = line && typeof line === 'object' ? line : {};
  const upper = (val) =>
    val === null || val === undefined ? null : String(val).trim().toUpperCase();
  const str = (val) =>
    val === null || val === undefined ? null : String(val);

  const widthMm  = num(safe.widthMm, 0);
  const heightMm = num(safe.heightMm, 0);
  const qty      = num(safe.qty, 1);

  let sqm = num(safe.sqm, 0);
  if (!(sqm > 0)) sqm = calculateLineSqm(widthMm, heightMm, qty);
  const sqft = num(safe.sqft, 0) > 0 ? num(safe.sqft, 0) : calculateLineSqft(sqm);

  return {
    itemId:            safe.itemId != null ? str(safe.itemId) : null,
    materialTypeCode:  upper(safe.materialTypeCode),
    materialTypeLabel: str(safe.materialTypeLabel),
    glassTypeCode:     upper(safe.glassTypeCode),
    glassTypeLabel:    str(safe.glassTypeLabel),
    thicknessCode:     safe.thicknessCode != null ? String(safe.thicknessCode).trim() : null,
    thicknessLabel:    str(safe.thicknessLabel),
    thicknessMm:       num(safe.thicknessMm, 0),
    categoryCode:      upper(safe.categoryCode),
    categoryLabel:     str(safe.categoryLabel),
    itemName:          str(safe.itemName),
    widthMm,
    heightMm,
    qty,
    sqm,
    sqft,
    chargeableSqm:     num(safe.chargeableSqm, 0),
    basicRate:         num(safe.basicRate, 0),
    basicAmount:       num(safe.basicAmount, 0),
    uom:               safe.uom != null ? String(safe.uom).trim() : null,
    // Line `type` and the SHEET group's part-shape scrap contribution must survive
    // normalization. This builder REBUILDS the line from a field whitelist, so any
    // field not listed here is silently dropped before the preview call. The engine
    // adds `sheetScrapForPartCharge` to the base for Jumbo/Shape/Template PART
    // charges (SHEET_PRICE / WASTAGE fold-in) and reads `type` for line rules — an
    // omitted field zeroes the contribution. See docs/proforma-sheet-line-rule.md item 5.
    type:                    safe.type != null ? str(safe.type) : null,
    sheetScrapForPartCharge: num(safe.sheetScrapForPartCharge, 0),
    // Per-ply SHAPE / TEMPLATE opt-in (LAMI/DGU builder) — unlocks the Shape /
    // Template charge on this ply. This builder whitelists fields, so without these
    // the flags would be dropped before the /preview call and the charge never fires.
    isShape:                 safe.isShape === true,
    isTemplate:              safe.isTemplate === true
  };
}


// ── preview payload builder ──────────────────────────────────────
export function buildPreviewPayload(lines, opts) {
  const options = opts && typeof opts === 'object' ? opts : {};
  const list = Array.isArray(lines) ? lines : [];
  const payload = {
    lines: list.map(normalizeLineItem)
  };
  if (Array.isArray(options.ruleIds)) {
    payload.ruleIds = options.ruleIds.filter((x) => x != null && x !== '');
  }
  if (Array.isArray(options.runtimeOverrides)) {
    payload.runtimeOverrides = options.runtimeOverrides
      .filter((o) => o && typeof o === 'object')
      .map((o) => {
        const out = { finalRate: num(o.finalRate, 0) };
        if (o.chargeCode != null && o.chargeCode !== '') {
          out.chargeCode = String(o.chargeCode).trim().toUpperCase();
        }
        if (o.ruleId != null && o.ruleId !== '') {
          out.ruleId = String(o.ruleId);
        }
        return out;
      });
  }
  if (options.includeInactive !== undefined) {
    payload.includeInactive = bool(options.includeInactive, false);
  }
  if (options.sourceModule != null && options.sourceModule !== '') {
    payload.sourceModule = String(options.sourceModule);
  }
  return payload;
}


// ── result-row rate override (recompute with finalRate >= 0) ─────
// Mirrors the backend amount math for an overridden rate. The
// override always recomputes chargeAmount from the row's existing
// quantity, then GST + total.
export function applyRateOverride(row, finalRate) {
  const safe = row && typeof row === 'object' ? row : {};
  const rate = Math.max(0, num(finalRate, 0));
  const quantity = num(safe.quantity, 0);
  const roundDigits = Number.isFinite(Number(safe.roundTo)) ? safe.roundTo : 2;

  const chargeAmount = roundTo(rate * quantity, roundDigits);
  const taxable = safe.taxable === true;
  const gstRate = num(safe.gstRate, 0);
  const gstAmount = taxable ? roundTo(chargeAmount * gstRate / 100, 2) : 0;

  return Object.assign({}, safe, {
    rate: roundTo(rate, Math.max(roundDigits, 2)),
    chargeAmount,
    taxable,
    gstRate,
    gstAmount,
    total: roundTo(chargeAmount + gstAmount, 2),
    overridden: true
  });
}


// ── summary (mirror backend summarizeOtherCharges) ───────────────
export function summarizeOtherCharges(rows) {
  const list = Array.isArray(rows) ? rows : [];
  let totalCharge = 0, totalGst = 0;
  const ruleIds = new Set();
  for (const r of list) {
    totalCharge += num(r && r.chargeAmount);
    totalGst += num(r && r.gstAmount);
    if (r && r.chargeCode) ruleIds.add(r.chargeCode);
  }
  const totalChargeAmount = roundTo(totalCharge, 2);
  const totalGstAmount = roundTo(totalGst, 2);
  return {
    totalChargeAmount,
    totalGstAmount,
    grandTotal: roundTo(totalChargeAmount + totalGstAmount, 2),
    ruleCount: ruleIds.size,
    rowCount: list.length
  };
}


// ── rule validation (mirror backend Joi messages + cross-field) ──
export function validateOtherChargeRule(formVals) {
  const v = formVals && typeof formVals === 'object' ? formVals : {};
  const errors = {};

  const chargeCode = v.chargeCode != null ? String(v.chargeCode).trim() : '';
  if (!chargeCode) {
    errors.chargeCode = 'Charge code is required.';
  }

  const chargeName = v.chargeName != null ? String(v.chargeName).trim() : '';
  if (!chargeName) {
    errors.chargeName = 'Charge name is required.';
  }

  if (v.ruleValue !== undefined && v.ruleValue !== null && v.ruleValue !== '') {
    if (num(v.ruleValue, 0) < 0) {
      errors.ruleValue = 'Rule value cannot be negative.';
    }
  }

  if (v.gstRate !== undefined && v.gstRate !== null && v.gstRate !== '') {
    const g = num(v.gstRate, 0);
    if (g < 0 || g > 28) {
      errors.gstRate = 'GST rate must be between 0 and 28.';
    }
  }

  const conditionType = v.conditionType ? String(v.conditionType).toUpperCase() : 'ALWAYS';

  if (conditionType === 'AREA_SLAB') {
    const hasSlab =
      (num(v.slab1Area) > 0 && num(v.slab1Value) > 0) ||
      (num(v.slab2Area) > 0 && num(v.slab2Value) > 0) ||
      (num(v.slab3Area) > 0 && num(v.slab3Value) > 0);
    if (!hasSlab) {
      errors.slab1Area = 'Area slab rule requires at least one slab.';
    }
  }

  if (SIZE_BASED_CONDITIONS.includes(conditionType)) {
    if (!(num(v.dimensionLimitMm) > 0)) {
      errors.dimensionLimitMm = 'Dimension limit is required for size-based conditions.';
    }
  }

  return { ok: Object.keys(errors).length === 0, errors };
}


// ── preview payload validation (mirror backend assertLines) ──────
export function validatePreviewPayload(payload) {
  const p = payload && typeof payload === 'object' ? payload : {};
  const errors = {};
  const lines = Array.isArray(p.lines) ? p.lines : [];

  if (lines.length === 0) {
    errors.lines = 'At least one line item is required for calculation.';
    return { ok: false, errors };
  }

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] || {};
    const isGlass = String(line.materialTypeCode || '').toUpperCase() === 'GLASS';
    const hasSqm = num(line.sqm) > 0;
    const hasWH = num(line.widthMm) > 0 && num(line.heightMm) > 0;
    if (isGlass && !hasSqm && !hasWH) {
      errors.lines = 'Width and height are required for glass SQM calculation.';
      break;
    }
  }

  return { ok: Object.keys(errors).length === 0, errors };
}


// ── enum display labels (enums, NOT business data) ───────────────
const CONDITION_TYPE_LABELS = Object.freeze({
  ALWAYS:           'Always',
  ANY_SIDE_EXCEEDS: 'Any side exceeds',
  WIDTH_EXCEEDS:    'Width exceeds',
  HEIGHT_EXCEEDS:   'Height exceeds',
  AREA_EXCEEDS:     'Area exceeds',
  AREA_SLAB:        'Area slab',
  SIZE_OR_AREA:     'Size or area',
  SIZE_AND_AREA:    'Size and area'
});

const CHARGE_BASE_LABELS = Object.freeze({
  PERCENT_OF_LINE_BASIC: '% of line basic',
  FIXED_PER_PCS:         'Fixed per piece',
  FIXED_PER_SQM:         'Fixed per SQM',
  FIXED_PER_SQFT:        'Fixed per SQFT',
  FIXED_PER_DOCUMENT:    'Fixed per document'
});

const VALUE_BY_LABELS = Object.freeze({
  PER_SQM_MM:      'Per SQM × mm',
  PER_MM:          'Per mm',
  PER_SQM:         'Per SQM',
  PER_SQFT:        'Per SQFT',
  PER_PCS:         'Per piece',
  PER_RFT:         'Per RFT',
  PER_RMT:         'Per RMT',
  PER_MTR:         'Per metre',
  FIXED:           'Fixed',
  CONDITION_BASED: 'Condition based'
});

const MODE_LABELS = Object.freeze({
  INR:        'INR',
  PERCENT:    'Percent',
  MULTIPLIER: 'Multiplier'
});

const DISPLAY_AS_LABELS = Object.freeze({
  LINE_WISE:      'Line-wise',
  THICKNESS_WISE: 'Thickness-wise',
  SINGLE_TOTAL:   'Single total'
});

function labelFrom(map, code) {
  if (code === null || code === undefined) return '';
  const key = String(code).trim().toUpperCase();
  return map[key] || key;
}

export function conditionTypeLabel(code) { return labelFrom(CONDITION_TYPE_LABELS, code); }
export function chargeBaseLabel(code)    { return labelFrom(CHARGE_BASE_LABELS, code); }
export function valueByLabel(code)       { return labelFrom(VALUE_BY_LABELS, code); }
export function modeLabel(code)          { return labelFrom(MODE_LABELS, code); }
export function displayAsLabel(code)     { return labelFrom(DISPLAY_AS_LABELS, code); }

// ════════════════════════════════════════════════════════════════
// Stage 6B.8A-3O — Formula + Area-Basis/UOM (pure frontend mirror of
// the backend otherChargesCalculation engine). No eval / Function.
// ════════════════════════════════════════════════════════════════
export const FORMULA_CODES     = Object.freeze(['PER_MM_X_AREA', 'PER_PCS', 'FIXED', 'RUNNING_PERCENT']);
export const UOMS              = Object.freeze(['SQM', 'SQFT', 'PCS', 'RFT', 'RMT', 'MTR', 'MM', 'NONE']);
export const QUANTITY_BASES    = Object.freeze(['ALL_QTY', 'T_PREFIX_QTY', 'NONE']);
export const SHOW_RESULT_MODES = Object.freeze(['THICKNESS_WISE', 'SINGLE_TOTAL']);
export const SOURCE_COLUMNS    = Object.freeze(['ACTUAL', 'CHARGEABLE', 'NONE']);

const FORMULA_CODE_LABELS = Object.freeze({
  PER_MM_X_AREA: 'Per MM × Area', PER_PCS: 'Per PCS', FIXED: 'Fixed', RUNNING_PERCENT: 'Running %'
});
const SOURCE_COLUMN_LABELS = Object.freeze({ ACTUAL: 'Actual', CHARGEABLE: 'Chargeable', NONE: 'None' });
const QUANTITY_BASIS_LABELS = Object.freeze({ ALL_QTY: 'All Qty', T_PREFIX_QTY: 'T- Prefix Qty', NONE: 'None' });
const SHOW_RESULT_LABELS = Object.freeze({ THICKNESS_WISE: 'Thickness Wise', SINGLE_TOTAL: 'Single Total' });
const UOM_LABELS = Object.freeze({
  SQM: 'SQM', SQFT: 'SQFT', PCS: 'PCS', RFT: 'RFT', RMT: 'RMT', MTR: 'MTR', MM: 'MM', NONE: 'None'
});

export function formulaCodeLabel(code)    { return labelFrom(FORMULA_CODE_LABELS, code); }
export function sourceColumnLabel(code)   { return labelFrom(SOURCE_COLUMN_LABELS, code); }
export function quantityBasisLabel(code)  { return labelFrom(QUANTITY_BASIS_LABELS, code); }
export function showResultModeLabel(code) { return labelFrom(SHOW_RESULT_LABELS, code); }
export function uomLabel(code)            { return labelFrom(UOM_LABELS, code); }

function n(v, fb = 0) { const x = Number(v); return Number.isFinite(x) ? x : fb; }

// source_column → which area feeds a formula (ACTUAL / CHARGEABLE / NONE).
export function resolveSourceArea(line, sourceColumn) {
  const sc = String(sourceColumn || 'CHARGEABLE').toUpperCase();
  const l = line || {};
  if (sc === 'NONE') return 0;
  if (sc === 'ACTUAL') {
    const a = n(l.actualSqm, 0);
    return a > 0 ? a : calculateLineSqm(l.widthMm, l.heightMm, l.qty);
  }
  const ch = n(l.chargeableSqm, 0);
  if (ch > 0) return ch;
  const a = n(l.actualSqm, 0);
  return a > 0 ? a : calculateLineSqm(l.widthMm, l.heightMm, l.qty);
}

export function isTPrefixLine(line) {
  const code = String((line && (line.itemCode || line.itemType || line.type)) || '')
    .trim().toUpperCase();
  return code.startsWith('T-');
}

// Quantity feeding a PER_PCS formula (ALL_QTY / T_PREFIX_QTY / NONE).
export function resolveFormulaQty(lines, quantityBasis) {
  const qb = String(quantityBasis || 'ALL_QTY').toUpperCase();
  if (qb === 'NONE') return 0;
  const arr = Array.isArray(lines) ? lines : (lines ? [lines] : []);
  let total = 0;
  for (const l of arr) {
    if (qb === 'T_PREFIX_QTY' && !isTPrefixLine(l)) continue;
    total += n(l && l.qty, 0);
  }
  return total;
}

// Declarative formula engine. Returns { rate, usedArea, usedQty, amount }.
// PER_MM_X_AREA: amount = source_area × thickness × rate/mm (rate shown = thk×rate).
// PER_PCS: amount = selected_qty × rate. FIXED: amount = rate.
// RUNNING_PERCENT: amount = running_total × rate / 100.
export function calculateByFormula(params) {
  const p = params || {};
  const formula = String(p.formulaCode || 'FIXED').toUpperCase();
  const rate = n(p.rate, 0);
  if (formula === 'PER_MM_X_AREA') {
    const usedArea = resolveSourceArea(p.line, p.sourceColumn);
    const thicknessMm = p.thicknessMm != null
      ? n(p.thicknessMm, 0)
      : n(p.line && p.line.thicknessMm, 0);
    const perSqmRate = thicknessMm * rate;
    return { rate: perSqmRate, usedArea, usedQty: 0, amount: usedArea * perSqmRate };
  }
  if (formula === 'PER_PCS') {
    const usedQty = resolveFormulaQty(p.lines != null ? p.lines : p.line, p.quantityBasis);
    return { rate, usedArea: 0, usedQty, amount: usedQty * rate };
  }
  if (formula === 'RUNNING_PERCENT') {
    const runningTotal = n(p.runningTotal, 0);
    return { rate, usedArea: 0, usedQty: 0, amount: runningTotal * rate / 100 };
  }
  return { rate, usedArea: 0, usedQty: 0, amount: rate };
}
